import { getSnowplowEnv, getSnowplowUserData } from '@/utils/analytics/analytics-utils';
import { SNOWPLOW } from '@/utils/analytics/constants';
import { KAMELEOON } from '@/utils/kameleoon/constants';
import { useEffect, useState } from 'react';
import { useKameleoonFlagsClient } from '@/hooks/use-kameleoon-flags-client';

const { CUSTOM_DATA } = KAMELEOON;

const setKameleoonCustomData = (kameleoonQueue: any, Kameleoon: any) => {
  const snowplowUser = getSnowplowUserData();
  window.kameleoonQueue = window.kameleoonQueue || [];

  // Push the command to set the VisitorCode with snowplow domainUserId
  window.kameleoonQueue.push({
    level: 'IMMEDIATE',
    command: () => Kameleoon.API.Visitor.setVisitorCode(snowplowUser.domainUserId),
  });

  // to get the current snowplow env
  const snowplowEnv = getSnowplowEnv().toUpperCase() as 'QA' | 'DEV' | 'PROD';

  //Setting the custom data required by snowplow
  kameleoonQueue.push(function () {
    Kameleoon.API.Data.setCustomData(CUSTOM_DATA.SP_APP_ID, SNOWPLOW.APP_ID[snowplowEnv]);
    Kameleoon.API.Data.setCustomData(CUSTOM_DATA.SP_DOMAIN_USERID, snowplowUser.domainUserId);
    Kameleoon.API.Data.setCustomData(CUSTOM_DATA.SP_DOMAIN_SESSIONID, snowplowUser.sessionId);
  });
};

//Kameleoon is an experimentation tool, hook is used to send custom data to snowplow for tracking
export const useKameleoonEngine = () => {
  const [isSnowplowAndKamReady, setIsSnowplowAndKamReady] = useState(false);
  const [checkForKamEngine, setCheckForKamEngine] = useState(false);
  const [numCheckForKamEngine, setNumCheckForKamEngine] = useState(0);

  // Use the new Kameleoon flags client hook (waits for cookie)
  // warehouseKey will be the same as visitorCode for anonymous users
  const { flags, isLoading, error, cookieAttempts } = useKameleoonFlagsClient({
    retryInterval: 2000, // Retry every 2 seconds
    maxRetries: 5, // Max 5 retries
    cookiePollInterval: 1000, // Check cookie every 1 second
    cookieMaxAttempts: 30, // Wait up to 30 seconds for cookie
  });

  useEffect(() => {
    if (!isSnowplowAndKamReady) {
      if (typeof window !== 'undefined' && window.__SHL__ && window.Kameleoon) {
        setIsSnowplowAndKamReady(true);
        //allow to only check for Kameleoon object max 5 times to avoid infinite loop if kam script does not load
      } else if (numCheckForKamEngine < 4) {
        //delay 1 second to allow for Kameleoon to load
        setTimeout(() => setCheckForKamEngine(!checkForKamEngine), 1000);
        setNumCheckForKamEngine(numCheckForKamEngine + 1);
      }
    }
  }, [checkForKamEngine, isSnowplowAndKamReady, numCheckForKamEngine]);

  useEffect(() => {
    const { Kameleoon, kameleoonQueue } = window;
    if (isSnowplowAndKamReady) {
      setKameleoonCustomData(kameleoonQueue, Kameleoon);
    }
  }, [isSnowplowAndKamReady]);

  // Log Kameleoon flags status
  useEffect(() => {
    if (Object.keys(flags).length > 0) {
      console.log('Kameleoon: Feature flags loaded successfully on client side');
    } else if (isLoading) {
      if (cookieAttempts > 0) {
        console.log(`Kameleoon: Waiting for visitor code cookie... (attempt ${cookieAttempts})`);
      } else {
        console.log('Kameleoon: Loading feature flags on client side...');
      }
    } else if (error) {
      console.warn('Kameleoon: Error loading feature flags on client side:', error);
    }
  }, [flags, isLoading, error, cookieAttempts]);

  return {
    isSnowplowAndKamReady,
    flags,
    isLoading,
    error,
  };
};
